/**
 * WorkersDatabase - Banco de dados interno de trabalhadores
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Consulta de dados históricos de trabalhadores
 * - Estatísticas de desempenho (produção, avaliações, tendências)
 * - Filtro por período (mês atual, todos, meses específicos)
 * - Notas descritivas e histórico de trabalho
 * - Sistema de avaliação interno (privado)
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, CardHeader, CardTitle, CardContent, Button (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Help icon component
const HelpIcon = ({ tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="ml-1 inline-flex items-center justify-center w-4 h-4 text-blue-500 hover:text-blue-700 transition-colors cursor-help">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200 z-50">
        <p className="text-sm leading-relaxed">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const WorkersDatabase = ({ property, month }) => {
  const { t } = useTranslation();
  const [workersData, setWorkersData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDatabase, setShowDatabase] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(month);

  useEffect(() => {
    setSelectedPeriod(month);
    setShowDatabase(false);
    setWorkersData(null);
  }, [month]);

  const loadWorkersDatabase = async () => {
    if (!property) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bleeding-records/${property.id}/workers-database?month=${selectedPeriod}`);
      setWorkersData(response.data);
      setShowDatabase(true);
    } catch (error) {
      console.error("Error loading workers database:", error);
      alert(t('wdbErrorLoading') + ": " + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  if (!property) return null;

  const monthLabels = {
    '01': t('wdbJan'), '02': t('wdbFeb'), '03': t('wdbMar'), '04': t('wdbApr'),
    '05': t('wdbMay'), '06': t('wdbJun'), '07': t('wdbJul'), '08': t('wdbAug'),
    '09': t('wdbSep'), '10': t('wdbOct'), '11': t('wdbNov'), '12': t('wdbDec')
  };

  return (
    <Card className="mb-6 border-blue-200 bg-blue-50" data-testid="workers-database">
      <CardHeader>
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="flex items-center text-blue-800">
            {t('wdbTitle')}
            <HelpIcon tooltip={t('tooltipWorkersDb')} />
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            <select 
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-1 border rounded text-sm flex-1 min-w-0 sm:flex-none"
              data-testid="workers-period-select"
            >
              <option value={month}>{t('wdbCurrentMonth')} ({month})</option>
              <option value="all">{t('wdbAllPeriods')}</option>
              {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                <option key={m} value={`2025-${m}`}>{monthLabels[m]} 2025</option>
              ))}
            </select>
            <Button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                loadWorkersDatabase();
              }} 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="workers-consult-btn"
            >
              {loading ? t('loading') : t('consultData')}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      
      {showDatabase && workersData && (
        <CardContent>
          <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg border">
              <h3 className="font-semibold text-blue-800 mb-2">{t('wdbSummary')}</h3>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div><strong>{t('wdbProperty')}:</strong> {workersData.property_name}</div>
                <div><strong>{t('wdbTotalRecords')}:</strong> {workersData.total_records}</div>
                <div><strong>{t('wdbWorkers')}:</strong> {workersData.total_workers}</div>
                <div><strong>{t('wdbPeriod')}:</strong> {workersData.period === 'all_time' ? t('wdbAllPeriodsLabel') : workersData.period}</div>
              </div>
            </div>

            {workersData.workers && workersData.workers.length > 0 ? (
              <div className="space-y-4">
                {workersData.workers.map((worker, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold text-gray-800 text-lg">{worker.name}</h4>
                      <div className="flex space-x-2">
                        {worker.average_score && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            worker.average_score >= 8 ? 'bg-green-100 text-green-800' :
                            worker.average_score >= 7 ? 'bg-blue-100 text-blue-800' :
                            worker.average_score >= 6 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {t('wdbAverage')}: {worker.average_score}/10
                          </span>
                        )}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`px-2 py-1 rounded text-xs font-medium cursor-help ${
                                worker.performance_trend === 'improving' ? 'bg-green-100 text-green-800' :
                                worker.performance_trend === 'declining' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {worker.performance_trend === 'improving' ? t('wdbImproving') :
                                 worker.performance_trend === 'declining' ? t('wdbDeclining') : t('wdbStable')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-white text-gray-800 p-2 rounded-lg shadow-xl border border-gray-200 z-50">
                              <p className="text-xs">
                                {worker.performance_trend === 'improving' ? t('tooltipTrendUp') :
                                 worker.performance_trend === 'declining' ? t('tooltipTrendDown') : t('tooltipTrendStable')}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-4 gap-4 mb-4 text-sm bg-gray-50 p-3 rounded">
                      <div><strong>{t('wdbDaysWorked')}:</strong> {worker.total_days_worked}</div>
                      <div><strong>{t('wdbTotalProd')}:</strong> {worker.total_production.toFixed(1)}kg</div>
                      <div><strong>{t('wdbDailyAvg')}:</strong> {worker.average_daily_production}kg</div>
                      <div><strong>{t('wdbEvaluations')}:</strong> {worker.numeric_evaluations.length} {t('wdbNumeric')}, {worker.descriptive_notes.length} {t('wdbDescriptive')}</div>
                    </div>

                    {worker.descriptive_notes.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-medium text-gray-700 mb-2">{t('wdbNotesObs')}:</h5>
                        <div className="space-y-2">
                          {worker.descriptive_notes.slice(0, 3).map((note, noteIndex) => (
                            <div key={noteIndex} className="bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">
                              <div className="text-xs text-gray-500 mb-1">{new Date(note.date).toLocaleDateString()}</div>
                              <div className="text-sm"><strong>{t('wdbEvaluation')}:</strong> {note.note}</div>
                              {note.observations && (
                                <div className="text-sm mt-1"><strong>{t('wdbObservations')}:</strong> {note.observations}</div>
                              )}
                            </div>
                          ))}
                          {worker.descriptive_notes.length > 3 && (
                            <div className="text-xs text-gray-500">... {t('wdbAndMore')} {worker.descriptive_notes.length - 3} {t('wdbRecords')}</div>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">{t('wdbLastWorks')}:</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="text-left p-1">{t('wdbDate')}</th>
                              <th className="text-center p-1">{t('wdbProduction')}</th>
                              <th className="text-center p-1">{t('wdbActivities')}</th>
                              <th className="text-left p-1">{t('wdbObservations')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {worker.work_history.slice(0, 5).map((day, dayIndex) => (
                              <tr key={dayIndex} className="border-t">
                                <td className="p-1">{new Date(day.date).toLocaleDateString()}</td>
                                <td className="text-center p-1">{day.production_kg}kg</td>
                                <td className="text-center p-1">
                                  {day.activities.sangria ? <img src="/icons/faca_sangria.png" alt="Sangria" className="w-4 h-4 inline" /> : ''} 
                                  {day.activities.fungicida ? '🧪' : ''}
                                  {day.activities.ethrel ? '💊' : ''}
                                </td>
                                <td className="p-1 text-xs">{day.observations || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>{t('wdbNoInternalEval')}</p>
                <p className="text-xs mt-2">{t('wdbMarkInternal')}</p>
              </div>
            )}

            <div className="flex justify-center pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDatabase(false)} data-testid="workers-close-btn">
                {t('closeDatabase')}
              </Button>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default WorkersDatabase;
